import React from 'react';
import { BROKER_BRAND, resolveBrokerKey } from './brokerBranding';
import ClientReportingSparkline from './ClientReportingSparkline';
import {
  CLIENT_REPORTING_DEMO_SERIES,
  CLIENT_REPORTING_DEMO_SPARKLINE,
  CLIENT_REPORTING_DEMO_SPARKLINE_DATES,
  CLIENT_REPORTING_DEMO_SUMMARY,
} from './clientReportingDemoPreview';
import { fmtCurrency, fmtPct, sentimentClass, sparklinePositive } from './clientReportingFormat';
import { brokerAccountDisplayName } from './clientReportingUtils';
import { IconInfo, IconChevronRight } from '../Icons';
import { PortfolioTableSkeleton } from './ClientReportingLoadSkeletons';

function AccountBadge({ client }) {
  const key = resolveBrokerKey(client.name, client.broker);
  const brand = key ? BROKER_BRAND[key] : null;
  if (brand?.logoSrc) {
    return (
      <span className={`vd-broker-logo ${brand.tone} has-image sm`} aria-hidden="true">
        <img src={brand.logoSrc} alt="" />
      </span>
    );
  }
  return (
    <span className="vd-broker-logo sm" aria-hidden="true">
      {brand?.shortName || (client.broker || '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

export default function ClientReportingPortfolioTable({
  clients,
  summariesById,
  sparklinesById,
  loading,
  onOpenPortfolio,
  onSelectPortfolio = null,
  selectedIds = [],
  defaultAccountId = null,
  onToggleDefault = null,
  onAddAccount = null,
  connectActionLabel = 'Add / Sync Trades',
}) {
  const selected = new Set(selectedIds);
  // Falls back to opening the detail page when no select handler is supplied,
  // so any other caller of this table keeps its old behavior.
  const handleRowActivate = onSelectPortfolio || onOpenPortfolio;
  return (
    <section className="vd-panel vd-ml-widget vd-ml-portfolios-panel">
      <div className="vd-ml-widget-header">
        <h3>Portfolios</h3>
      </div>

      <div
        className="vd-ml-portfolios-table-wrap"
        aria-busy={loading ? 'true' : undefined}
      >
        <table className="vd-ml-portfolios-table">
          <thead>
            <tr>
              <th scope="col" className="vd-ml-portfolios-name-col">Name</th>
              <th scope="col" className="vd-ml-portfolios-gain-col">Gain</th>
              <th scope="col" className="vd-ml-portfolios-profit-col">Profit</th>
              <th scope="col" className="vd-ml-portfolios-change-col">Change</th>
              <th scope="col" className="vd-ml-portfolios-chart-head">
                Chart
                <span className="vd-ml-portfolios-chart-info" title="Equity curve for this account">
                  <IconInfo size={12} color="currentColor" />
                </span>
              </th>
            </tr>
          </thead>
          {loading && clients.length ? (
            <PortfolioTableSkeleton rows={Math.min(Math.max(clients.length, 1), 4)} />
          ) : (
          <tbody>
            {clients.map((client) => {
              const summary = summariesById[client.id] || {};
              const gain = summary.gain?.total;
              const profit = summary.profit?.total;
              // Tolerates the legacy shape (a bare array) so a stale cached
              // entry renders instead of throwing.
              const spark = sparklinesById[client.id];
              const sparkValues = Array.isArray(spark) ? spark : (spark?.values || []);
              const sparkDates = Array.isArray(spark) ? null : (spark?.dates || null);
              const positive = sparklinePositive(gain) ?? (
                sparkValues.length >= 2
                  ? sparkValues[sparkValues.length - 1] >= sparkValues[0]
                  : true
              );
              const displayName = brokerAccountDisplayName(client);
              const isSelected = selected.has(client.id);

              return (
                <tr
                  key={client.id}
                  className={`vd-ml-portfolio-row${isSelected ? ' is-selected' : ''}`}
                  // A selector, not a link: activating a row changes what the
                  // dashboard below is showing rather than navigating away.
                  role="button"
                  aria-pressed={isSelected}
                  tabIndex={0}
                  onClick={() => handleRowActivate(client.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowActivate(client.id);
                    }
                  }}
                  aria-label={`Show ${displayName} on the dashboard`}
                >
                  <td className="vd-ml-portfolios-name-col">
                    <span className="vd-ml-portfolio-name">
                      <AccountBadge client={client} />
                      <span className="vd-ml-portfolio-name-text">{displayName}</span>
                      {/* stopPropagation: the row selects the portfolio, and
                          pinning a default should not also change the view. */}
                      {onToggleDefault ? (
                        <button
                          type="button"
                          className={`vd-ml-portfolio-default${defaultAccountId === client.id ? ' is-default' : ''}`}
                          onClick={(e) => { e.stopPropagation(); onToggleDefault(client.id); }}
                          onKeyDown={(e) => e.stopPropagation()}
                          title={defaultAccountId === client.id
                            ? 'Default portfolio — click to unset'
                            : 'Make this the default portfolio'}
                          aria-pressed={defaultAccountId === client.id}
                          aria-label={defaultAccountId === client.id
                            ? `${displayName} is your default portfolio`
                            : `Make ${displayName} your default portfolio`}
                        >
                          {defaultAccountId === client.id ? '★' : '☆'}
                        </button>
                      ) : null}
                    </span>
                  </td>
                  <td className={`vd-ml-portfolios-gain-col ${sentimentClass(gain)}`}>{fmtPct(gain)}</td>
                  <td className={`vd-ml-portfolios-profit-col ${sentimentClass(profit)}`}>{fmtCurrency(profit, { signed: true })}</td>
                  <td className={`vd-ml-portfolios-change-col ${sentimentClass(gain)}`}>{fmtPct(gain)}</td>
                  <td className="vd-ml-portfolios-chart-cell">
                    <div className="vd-ml-portfolios-sparkline-wrap">
                      {/* No curve yet because it's still on its way (the broker's
                          history is loading, or the sparklines haven't arrived):
                          say so instead of leaving the cell empty. */}
                      {sparkValues.length < 2 && (client.historyPending || (loading && spark === undefined)) ? (
                        <span className="vd-ml-portfolios-spark-loading" role="status">
                          <span className="vd-ml-portfolios-spark-dot" aria-hidden="true" />
                          {client.historyPending ? 'Loading history…' : 'Loading chart…'}
                        </span>
                      ) : (
                        <ClientReportingSparkline values={sparkValues} dates={sparkDates} positive={positive} wide />
                      )}
                    </div>
                    {/* stopPropagation: the row selects, this navigates. Pinned
                        over the sparkline so the table never needs horizontal
                        scroll to reach it. */}
                    <button
                      type="button"
                      className="vd-ml-portfolio-details vd-ml-portfolio-details--overlay"
                      onClick={(e) => { e.stopPropagation(); onOpenPortfolio(client.id); }}
                      onKeyDown={(e) => e.stopPropagation()}
                      aria-label={`Open the ${displayName} portfolio page`}
                      title={`Open the ${displayName} portfolio page`}
                    >
                      Details
                      <IconChevronRight size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          )}
        </table>

        {!loading && !clients.length ? (
          <div className="vd-cr-connect-empty vd-cr-connect-empty--portfolios">
            <table className="vd-ml-portfolios-table vd-cr-demo-table" aria-hidden="true">
              <tbody>
                <tr className="vd-cr-demo-row">
                  <td>
                    <span className="vd-broker-logo sm neutral" aria-hidden="true">D</span>
                    {CLIENT_REPORTING_DEMO_SERIES}
                  </td>
                  <td className="vd-positive">{fmtPct(CLIENT_REPORTING_DEMO_SUMMARY.gain.total)}</td>
                  <td className="vd-positive">{fmtCurrency(CLIENT_REPORTING_DEMO_SUMMARY.profit.total, { signed: true })}</td>
                  <td className="vd-ml-portfolios-chart-cell">
                    <div className="vd-ml-portfolios-sparkline-wrap">
                      <ClientReportingSparkline
                        values={CLIENT_REPORTING_DEMO_SPARKLINE}
                        dates={CLIENT_REPORTING_DEMO_SPARKLINE_DATES}
                        positive
                        wide
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="vd-cr-connect-overlay vd-cr-connect-overlay--inset vd-cr-connect-overlay--button-only">
              <button type="button" className="vd-btn vd-client-sync-btn" onClick={onAddAccount}>
                {connectActionLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
